// "Talk to HR": an employee hands a question to a person. The request carries who they are (the
// only place Ask HR attaches identity to what someone asked), a category, an optional note, and,
// if they choose, the conversation it came from. HR is emailed and works it in the Requests queue.
// The employee can follow it in Ask HR: its status, and any comment HR addresses to them. HR's own
// notes stay with HR. Most answers go by email, which Ask HR doesn't see.

import { BadRequestError } from "./sources.js";

export const CATEGORIES = {
  leave: "Leave or time off",
  pay: "Pay or benefits",
  policy: "A policy question",
  concern: "A workplace concern",
  other: "Something else",
} as const;

export type Category = keyof typeof CATEGORIES;
export type EscalationStatus = "new" | "in_progress" | "resolved";
export type Channel = "web" | "slack";

export const STATUS_LABELS: Record<EscalationStatus, string> = {
  new: "New",
  in_progress: "In progress",
  resolved: "Resolved",
};

export interface ExcerptTurn {
  role: "user" | "assistant";
  text: string;
}

// A file HR added to a note or comment. Stored at attachmentKey(request, id); downloaded through
// a short-lived link the API hands out to HR, or to the requester when it's on a comment.
export interface Attachment {
  id: string;
  name: string;
  type: string; // one of ATTACHMENT_TYPES
  size: number; // bytes, as stored
}

export interface EscalationEvent {
  at: string;
  by: string;
  status?: EscalationStatus;
  note?: string;
  // "employee": the note is a comment the requester sees. Absent: a note for HR only (every note
  // written before comments existed is one).
  visibility?: "employee";
  attachments?: Attachment[];
  // "employee": the requester wrote it (a reply, on the web or in Slack). Absent: HR did.
  author?: "employee";
}

// What HR may attach: documents and pictures, never anything a browser would run.
export const ATTACHMENT_TYPES: Record<string, string> = {
  "application/pdf": "PDF",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "Word",
  "application/msword": "Word",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "Excel",
  "application/vnd.ms-excel": "Excel",
  "text/csv": "CSV",
  "text/plain": "Text",
  "image/png": "PNG",
  "image/jpeg": "JPEG",
};
export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;
export const MAX_ATTACHMENTS = 5;
export const isAttachmentType = (t: unknown): t is string => typeof t === "string" && Object.hasOwn(ATTACHMENT_TYPES, t);
// A file's type by its extension (browsers and Slack don't always say, or say "octet-stream"),
// else the type it came with if that's allowed. Undefined: not a file Ask HR takes.
const BY_EXTENSION: Record<string, string> = {
  pdf: "application/pdf",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  csv: "text/csv",
  txt: "text/plain",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
};
export const ATTACHMENT_EXTENSIONS = Object.keys(BY_EXTENSION);
export function attachmentTypeFor(name: string, type?: string): string | undefined {
  const ext = name.split(".").pop()?.toLowerCase() ?? "";
  return BY_EXTENSION[ext] ?? (isAttachmentType(type) ? type : undefined);
}

export const attachmentKey = (requestId: string, id: string) => `uploads/requests/${requestId}/${id}`;

// A file name safe to store and show: no folders, control characters or quotes, at most 120
// characters (keeping the extension).
export function attachmentName(raw: unknown): string {
  const base = (typeof raw === "string" ? raw : "").split(/[\\/]/).pop() ?? "";
  let name = "";
  for (const ch of base) {
    const c = ch.codePointAt(0) ?? 0;
    name += c < 32 || c === 127 || ch === '"' ? "_" : ch;
  }
  name = name.trim();
  if (!name || name === "." || name === "..") return "attachment";
  if (name.length <= 120) return name;
  const dot = name.lastIndexOf(".");
  const ext = dot > 0 && name.length - dot <= 10 ? name.slice(dot) : "";
  return name.slice(0, 120 - ext.length) + ext;
}

export function attachmentSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export interface Escalation {
  id: string;
  status: EscalationStatus;
  category: Category;
  note: string;
  channel: Channel;
  requester: { oid: string; name: string; email: string };
  location: string; // "California", "Another US state", "Canada", or "Not set"
  conversationId?: string;
  excerpt: ExcerptTurn[];
  createdAt: string;
  updatedAt: string;
  history: EscalationEvent[];
  emailed: boolean;
  // When the employee removed it from their list in Ask HR. HR keeps the request and sees this.
  hiddenAt?: string;
}

export interface EscalationInput {
  category: Category;
  note: string;
  conversationId?: string;
  includeConversation: boolean;
}

// Telling an employee HR commented on their request: by email and in Slack, with a link and not
// the comment itself (it stays in Ask HR, where only they and HR can see it).
export interface CommentNotice {
  kind: "notify_comment";
  email: string;
  requestId: string;
  category: Category;
}

export function commentNoticeText(category: Category, url: string): { subject: string; text: string; html: string } {
  const about = CATEGORIES[category].toLowerCase();
  const text = `HR added a comment to your request about ${about}. Read it in Ask HR: ${url}`;
  const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
  return {
    subject: "HR commented on your request",
    text: `${text}\n\nReply in Ask HR (you can attach files there), so HR sees it on your request.`,
    html: `<p>HR added a comment to your request about ${esc(about)}.</p><p><a href="${esc(url)}">Read it in Ask HR</a></p><p style="color:#666;font-size:12px">Reply in Ask HR (you can attach files there), so HR sees it on your request.</p>`,
  };
}

// Telling HR the requester replied: email to HR and the HR Slack channel, a link and not the reply.
export interface ReplyNotice {
  kind: "notify_reply";
  requestId: string;
  category: Category;
  name: string;
}

export function replyNoticeEmail(e: Escalation, appUrl: string): { subject: string; text: string; html: string } {
  const link = `${appUrl}/admin/requests/${e.id}`;
  const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
  return {
    subject: `Ask HR: ${e.requester.name} replied (${CATEGORIES[e.category]})`,
    text: [`${e.requester.name} replied on their Talk to HR request: ${CATEGORIES[e.category]}.`, "", `Read it and answer in Ask HR: ${link}`].join("\n"),
    html: [
      `<p><strong>${esc(e.requester.name)}</strong> replied on their Talk to HR request: <strong>${esc(CATEGORIES[e.category])}</strong>.</p>`,
      `<p><a href="${esc(link)}">Read it and answer in Ask HR</a>.</p>`,
    ].join("\n"),
  };
}

// What the requester sees of their own request. Never HR's notes, never other requests.
export interface EmployeeRequest {
  id: string;
  status: EscalationStatus;
  category: Category;
  createdAt: string;
  updatedAt: string;
  summary: string; // the start of their note, for the list and its search
  comments: number;
  lastCommentAt?: string;
}

export interface EmployeeTimelineEntry {
  at: string;
  status?: EscalationStatus;
  // mine: their own reply; otherwise a comment from HR.
  comment?: { by: string; text: string; attachments?: Attachment[]; mine?: true };
}

export interface EmployeeRequestDetail extends EmployeeRequest {
  note: string;
  channel: Channel;
  conversationId?: string;
  excerpt: ExcerptTurn[];
  timeline: EmployeeTimelineEntry[];
}

// Shared with the requester: HR's comments and their own replies.
const isShared = (h: EscalationEvent) => h.visibility === "employee" && Boolean(h.note || h.attachments?.length);
// HR's comments only: what "updates" and the unseen badge count.
const isComment = (h: EscalationEvent) => isShared(h) && h.author !== "employee";

// An attachment the requester may download: on a comment to them, never on an HR-only note.
export function employeeAttachment(e: Escalation, id: string): Attachment | undefined {
  return e.history.filter(isShared).flatMap((h) => h.attachments ?? []).find((a) => a.id === id);
}

export function findAttachment(e: Escalation, id: string): Attachment | undefined {
  return e.history.flatMap((h) => h.attachments ?? []).find((a) => a.id === id);
}

export function forEmployee(e: Escalation): EmployeeRequest {
  const comments = e.history.filter(isComment);
  const last = comments.at(-1);
  return {
    id: e.id,
    status: e.status,
    category: e.category,
    createdAt: e.createdAt,
    updatedAt: e.updatedAt,
    summary: e.note.length > 140 ? `${e.note.slice(0, 140)}…` : e.note,
    comments: comments.length,
    ...(last ? { lastCommentAt: last.at } : {}),
  };
}

export function forEmployeeDetail(e: Escalation): EmployeeRequestDetail {
  const timeline: EmployeeTimelineEntry[] = [];
  for (const h of e.history) {
    const comment = isShared(h)
      ? {
          by: h.by,
          text: h.note ?? "",
          ...(h.attachments?.length ? { attachments: h.attachments } : {}),
          ...(h.author === "employee" ? { mine: true as const } : {}),
        }
      : undefined;
    // An HR-only note that came with a status change still shows the change, without the note.
    if (!comment && !h.status) continue;
    timeline.push({ at: h.at, ...(h.status ? { status: h.status } : {}), ...(comment ? { comment } : {}) });
  }
  return {
    ...forEmployee(e),
    note: e.note,
    channel: e.channel,
    ...(e.conversationId ? { conversationId: e.conversationId } : {}),
    excerpt: e.excerpt,
    timeline,
  };
}

export const MAX_NOTE_CHARS = 2000;
export const MAX_EXCERPT_TURNS = 12;

export function isCategory(v: unknown): v is Category {
  return typeof v === "string" && Object.hasOwn(CATEGORIES, v);
}

export function isStatus(v: unknown): v is EscalationStatus {
  return v === "new" || v === "in_progress" || v === "resolved";
}

export function parseEscalationInput(body: unknown): EscalationInput {
  const b = (typeof body === "object" && body !== null ? body : {}) as Record<string, unknown>;
  if (!isCategory(b.category)) throw new BadRequestError("Choose what this is about.");
  const note = typeof b.note === "string" ? b.note.trim() : "";
  if (note.length > MAX_NOTE_CHARS) throw new BadRequestError(`Notes can be up to ${MAX_NOTE_CHARS} characters.`);
  const conversationId = typeof b.conversationId === "string" && b.conversationId ? b.conversationId : undefined;
  const includeConversation = b.includeConversation !== false && Boolean(conversationId);
  if (!note && !includeConversation) throw new BadRequestError("Add a note, or include the conversation, so HR knows what you need.");
  return { category: b.category, note, ...(conversationId ? { conversationId } : {}), includeConversation };
}

// ---- the email to HR: a notice with a link, never the request itself. HR reads and answers it
// in the portal, so every reply is on the request and tracked (and the employee is told there).

const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

export function escalationEmail(e: Escalation, appUrl: string): { subject: string; text: string; html: string } {
  const link = `${appUrl}/admin/requests/${e.id}`;
  // Not the employee's name: the email is from Ask HR (askhr@novara.com) and shouldn't read as theirs.
  const subject = `New Talk to HR request: ${CATEGORIES[e.category]}`;
  const from = e.channel === "slack" ? " from Slack" : "";
  const text = [
    `${e.requester.name} sent a new Talk to HR request${from}: ${CATEGORIES[e.category]}.`,
    "",
    `Open it in Ask HR to read it and reply: ${link}`,
    "",
    "Reply in Ask HR rather than by email, so the conversation stays on the request and is tracked.",
  ].join("\n");
  const html = [
    `<p><strong>${esc(e.requester.name)}</strong> sent a new Talk to HR request${from}: <strong>${esc(CATEGORIES[e.category])}</strong>.</p>`,
    `<p><a href="${esc(link)}">Open it in Ask HR</a> to read it and reply.</p>`,
    `<p>Reply in Ask HR rather than by email, so the conversation stays on the request and is tracked.</p>`,
  ].join("\n");
  return { subject, text, html };
}
